/* client/src/components/ProfitManagement.tsx - UPDATED TO USE FRONTEND DATA ONLY */
import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Box,
  Alert,
  CircularProgress,
  Chip,
  Tabs,
  Tab,
  IconButton,
  Tooltip
} from '@mui/material';
import { formatCurrency, formatNumber } from '../utils/formatters';
import {
  apiGetBusinessUnits,
  apiGetTotalInvestmentAll,
  apiGetTotalInventory,
  apiGetProfitSummary,
  apiGetProfitCalculation, 
  apiGetPartnershipShares,
  apiGetBookedProfitTotal,
  apiGetProfitBookings,
  apiGetProfitWithdrawals,
  apiBookProfit,
  apiWithdrawProfit,
  apiGetInvestmentTotalsByBusinessUnit,
  apiGetInvestments,      
  apiGetExpenses
} from '../services/apiService';
import { binanceService } from '../services/binanceService';
import {
  Add as AddIcon,
  AccountBalanceWallet as WithdrawIcon,
  ShowChart as ProfitIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon
} from '@mui/icons-material';

interface ProvisionalProfitData {
  business_unit_id: number;
  business_unit_name: string;
  capital_balance: number;
  inventory_value: number;
  provisional_profit: number;
  gold_rate: number;
  share_percentage: number;
  partner_share: number;
  already_booked: number;
  available_for_booking: number;
  total_investment?: number;
  total_expenses?: number;
}

interface ProfitBooking {
  id: number;
  business_unit_id: number;
  business_unit_name: string;
  booking_date: string;
  amount: number;
  description: string;
  status: string;
  gold_rate: number;
  inventory_value: number;
  capital_balance: number;
  created_at: string;
}

interface ProfitWithdrawal {
  id: number;
  profit_booking_id: number;
  withdrawal_date: string;
  amount: number;
  description: string;
  status: string;
  created_at: string;
}

interface ProfitSummary {
  business_unit_id: number;
  business_unit_name: string;
  share_percentage: number;
  provisional_profit: number;
  partner_provisional_share: number; 
  booked_profit: number;
  withdrawn_profit: number;
  available_for_withdrawal: number;
  balance_share_amount: number;
}

interface ProfitSummaryTotals {
  total_provisional_profit: number;
  total_partner_share: number;
  total_booked_profit: number;
  total_withdrawn_profit: number;
  total_available_for_withdrawal: number;
  total_balance_share_amount: number;
}

interface CalculationBreakdown {
  inventory_value: number;
  total_investment: number;
  total_expenses: number;
  provisional_profit: number;
}

interface LiveGoldRate {
  goldPerGram: number;
  goldPerOz: number;
  lastUpdated: string;
  source: string;
}

interface InventorySummaryData {
  unit: string;
  quantity: number; // in grams
  value: number;
  average_rate: number;
  business_unit_id?: number;
}

function ProfitManagement() {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [businessUnits, setBusinessUnits] = useState<any[]>([]);
  const [selectedUnit, setSelectedUnit] = useState('');
  const [provisionalData, setProvisionalData] = useState<ProvisionalProfitData | null>(null);
  const [calculationBreakdown, setCalculationBreakdown] = useState<CalculationBreakdown | null>(null);
  const [bookings, setBookings] = useState<ProfitBooking[]>([]);
  const [withdrawals, setWithdrawals] = useState<ProfitWithdrawal[]>([]);
  const [summary, setSummary] = useState<ProfitSummary[]>([]);
  const [summaryTotals, setSummaryTotals] = useState<ProfitSummaryTotals>({
    total_provisional_profit: 0,
    total_partner_share: 0,
    total_booked_profit: 0,
    total_withdrawn_profit: 0,
    total_available_for_withdrawal: 0,
    total_balance_share_amount: 0
  });
  const [bookingOpen, setBookingOpen] = useState(false);
  const [withdrawalOpen, setWithdrawalOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<ProfitBooking | null>(null);
  const [totalInvestment, setTotalInvestment] = useState<number>(0);
  const [liveGoldRate, setLiveGoldRate] = useState<LiveGoldRate | null>(null);
  const [refreshingGoldRate, setRefreshingGoldRate] = useState(false);
  const [inventoryData, setInventoryData] = useState<InventorySummaryData[]>([]);

  const [bookingForm, setBookingForm] = useState({
    business_unit_id: '',
    booking_date: new Date().toISOString().split('T')[0],
    amount: '',
    description: ''
  });

  const [withdrawalForm, setWithdrawalForm] = useState({
    profit_booking_id: '',
    withdrawal_date: new Date().toISOString().split('T')[0],
    amount: '',
    description: ''
  });

  const backendUrl = process.env.REACT_APP_API_URL || 'https://api.koodo.tech';

  // Function to fetch live gold rate from Binance
  const fetchLiveGoldRate = async (): Promise<LiveGoldRate | null> => {
    try {
      const goldPrice = await binanceService.getGoldPrice();
      
      const usdToAed = 3.67;
      const ozToGram = 31.1034768;
      
      const goldPerOz = goldPrice.price * usdToAed;
      const goldPerGram = goldPerOz / ozToGram;
      
      return {
        goldPerGram: parseFloat(goldPerGram.toFixed(2)),
        goldPerOz: parseFloat(goldPerOz.toFixed(2)),
        lastUpdated: new Date().toISOString(),
        source: 'Binance API (Live)'
      };
    } catch (error) {
      console.error('Error fetching live gold rate:', error);
      return null;
    }
  };
const calculateProvisionalProfit = async () => {
  
 try {
    setLoading(true);
    setError('');
    
    if (!selectedUnit || selectedUnit === '') {
      throw new Error('Please select a business unit first');
    }

    if (!liveGoldRate?.goldPerGram) {
      throw new Error('Please refresh the gold rate first');
    }
    
    const currentGoldRate = liveGoldRate.goldPerGram;
    const businessUnitId = parseInt(selectedUnit);
    
    console.log('🔍 Calculating profit for Unit', businessUnitId);

    // ✅ FIX: Get accurate inventory weight for THIS unit only
    const totalWeightGrams = getInventoryWeightForUnit(businessUnitId);
    console.log('🔍 Inventory weight for this unit:', totalWeightGrams, 'grams');

    // Calculate inventory value using dynamic weight
    const inventoryValue = totalWeightGrams * currentGoldRate;
    console.log('🔍 Calculated inventory value:', inventoryValue);

    // ✅ DYNAMIC FIX: Handle any API response structure without hardcoding
let totalInvestment = 0;
let totalExpenses = 0;
let businessUnitName = `Unit ${String.fromCharCode(64 + businessUnitId)}`;

// Helper function to safely extract investment data
const extractInvestmentData = (data: any, targetUnitId: number): number => {
  if (!data) return 0;
  
  // Case 1: Array of business unit objects
  if (Array.isArray(data)) {
    const unitData = data.find((item: any) => item.business_unit_id === targetUnitId);
    return unitData?.total_transferred || unitData?.transferred_amount || unitData?.amount || 0;
  }
  
  // Case 2: Object with business_units array
  if (data.business_units && Array.isArray(data.business_units)) {
    const unitData = data.business_units.find((item: any) => item.business_unit_id === targetUnitId);
    return unitData?.total_transferred || unitData?.transferred_amount || unitData?.amount || 0;
  }
  
  // Case 3: Object keyed by business_unit_id
  if (data[targetUnitId]) {
    return data[targetUnitId].total_transferred || data[targetUnitId].transferred_amount || data[targetUnitId].amount || 0;
  }
  
  // Case 4: Single object (if API returns just one unit's data)
  if (data.business_unit_id === targetUnitId) {
    return data.total_transferred || data.transferred_amount || data.amount || 0;
  }
  
  return 0;
};

try {
  // Method 1: Try investment totals API first (most reliable)
  try {
    const investmentTotals = await apiGetInvestmentTotalsByBusinessUnit();
    totalInvestment = extractInvestmentData(investmentTotals, businessUnitId);
    
    console.log('🔍 Investment totals extraction result:', totalInvestment);
  } catch (totalsError) {
    console.error('❌ Investment totals API failed, trying individual investments:', totalsError);
    
    // Method 2: Fallback to individual investments API
    try {
      const unitInvestments = await apiGetInvestments(businessUnitId);
      
      if (Array.isArray(unitInvestments)) {
        // Sum all investments for this business unit
        totalInvestment = unitInvestments.reduce((sum: number, investment: any) => {
          // Only count if it belongs to this business unit
          if (investment.business_unit_id === businessUnitId) {
            return sum + (investment.amount_transferred || investment.amount || 0);
          }
          return sum;
        }, 0);
      }
      
      console.log('🔍 Individual investments calculation result:', totalInvestment);
    } catch (investmentsError) {
      console.error('❌ Individual investments API also failed:', investmentsError);
      totalInvestment = 0;
    }
  }

  // Get expenses using similar dynamic approach
  try {
    const expensesData = await apiGetExpenses(businessUnitId);
    
    if (Array.isArray(expensesData)) {
      totalExpenses = expensesData.reduce((sum: number, expense: any) => {
        if (expense.business_unit_id === businessUnitId) {
          return sum + (expense.amount || 0);
        }
        return sum;
      }, 0);
    }
    
  } catch (expenseError) {
    console.error('❌ Expense fetch error:', expenseError);
    totalExpenses = 0;
  }
  
} catch (investmentError) {
  console.error('❌ Comprehensive investment data fetch failed:', investmentError);
  totalInvestment = 0;
  totalExpenses = 0;
}

console.log('🔍 ✅ FINAL dynamic calculation for Unit', businessUnitId, ':', {
  totalInvestment,
  totalExpenses
});
    // Calculate provisional profit
    const provisionalProfit = Math.max(0, inventoryValue - totalInvestment - totalExpenses);
    
    console.log('🔍 ✅ FINAL CORRECT calculation for this unit:', {
      businessUnitId,
      inventoryValue,
      totalInvestment, // This should now be correct (0 for Unit B)
      totalExpenses,
      provisionalProfit
    });

    // Get partner share percentage for THIS unit
    let sharePercentage = 0;
    try {
      const partnerData = await apiGetPartnershipShares(businessUnitId);
      sharePercentage = partnerData.percentage || 0;
      businessUnitName = partnerData.business_unit_name || businessUnitName;
    } catch (partnerError) {
      sharePercentage = businessUnitId === 1 ? 33 : 16;
    }

    const partnerShare = provisionalProfit * (sharePercentage / 100);

    // Get already booked profit for THIS unit
    let alreadyBooked = 0;
    try {
      const bookedData = await apiGetBookedProfitTotal(businessUnitId);
      alreadyBooked = bookedData.total_booked || 0;
    } catch (bookedError) {
      alreadyBooked = 0;
    }

    const availableForBooking = Math.max(0, partnerShare - alreadyBooked);

    // Set data with correct calculations
    setProvisionalData({
      business_unit_id: businessUnitId,
      business_unit_name: businessUnitName,
      capital_balance: totalInvestment,
      inventory_value: inventoryValue,
      provisional_profit: provisionalProfit,
      gold_rate: currentGoldRate,
      share_percentage: sharePercentage,
      partner_share: partnerShare,
      already_booked: alreadyBooked,
      available_for_booking: availableForBooking,
      total_investment: totalInvestment,
      total_expenses: totalExpenses
    });

    setCalculationBreakdown({
      inventory_value: inventoryValue,
      total_investment: totalInvestment,
      total_expenses: totalExpenses,
      provisional_profit: provisionalProfit
    });

  } catch (err: any) {
    console.error('❌ ERROR in calculateProvisionalProfit:', err);
    setError(err.message || 'Failed to calculate provisional profit');
    setProvisionalData(null);
    setCalculationBreakdown(null);
  } finally {
    setLoading(false);
  }
};

  // Function to refresh gold rate
  const refreshGoldRate = async () => {
    setRefreshingGoldRate(true);
    try {
      const rate = await fetchLiveGoldRate();
      if (rate) {
        setLiveGoldRate(rate);
        setSuccess('Gold rate updated successfully');
        
        if (selectedUnit) {
          await calculateProvisionalProfit();
        }
      }
    } catch (error) {
      console.error('Error refreshing gold rate:', error);
      setError('Failed to refresh gold rate');
    } finally {
      setRefreshingGoldRate(false);
    }
  };

  // Fetch dynamic inventory data
  const fetchInventoryData = async () => {
    try {
      console.log('📊 Fetching dynamic inventory data...');
      const inventoryResponse = await apiGetTotalInventory();
      console.log('📊 Inventory API Response:', inventoryResponse);
      
      if (inventoryResponse && inventoryResponse.business_units) {
        const formattedData = inventoryResponse.business_units.map((unit: any) => ({
          unit: unit.unit || 'Unknown',
          quantity: unit.quantity || 0, // in grams
          value: unit.value || 0,
          average_rate: unit.average_rate || 0,
          business_unit_id: unit.business_unit_id || getBusinessUnitIdFromName(unit.unit)
        }));
        
        setInventoryData(formattedData);
        console.log('📊 Formatted Inventory Data:', formattedData);
      } else {
        console.error('Invalid inventory data structure');
        setInventoryData([]);
      }
    } catch (err) {
      console.error('Error fetching inventory data:', err);
      setInventoryData([]);
    }
  };

  // Helper function to get business unit ID from name
  const getBusinessUnitIdFromName = (unitName: string): number => {
    if (unitName.includes('A') || unitName.includes('1')) return 1;
    if (unitName.includes('B') || unitName.includes('2')) return 2;
    if (unitName.includes('C') || unitName.includes('3')) return 3;
    return 0;
  };

  // Get inventory weight for a business unit dynamically
  const getInventoryWeightForUnit = (businessUnitId: number): number => {
    if (!inventoryData || inventoryData.length === 0) {
      console.log('📊 No inventory data available');
      return 0;
    }
    console.log('📊 All inventory data for matching:', inventoryData);

    // ✅ FIX 1: Exact business unit ID match
    const exactMatch = inventoryData.find(item => item.business_unit_id === businessUnitId);
    if (exactMatch) {
      console.log(`📊 Exact ID match for Unit ${businessUnitId}:`, exactMatch.quantity, 'grams');
      return exactMatch.quantity;
    }

    // ✅ FIX 2: Name-based match (case-insensitive)
    const unitNames = [
      `Unit ${String.fromCharCode(64 + businessUnitId)}`,
      `Unit ${businessUnitId}`,
      `Business Unit ${String.fromCharCode(64 + businessUnitId)}`,
      `Business Unit ${businessUnitId}`
    ];

    const nameMatch = inventoryData.find(item =>
      item.unit && unitNames.some(name =>
        item.unit.trim().toLowerCase().includes(name.toLowerCase())
      )
    );

    if (nameMatch) {
      console.log(`📊 Name match for Unit ${businessUnitId}:`, nameMatch.quantity, 'grams');
      return nameMatch.quantity;
    }

    // ✅ FIX 4: Fallback to total inventory ONLY for Unit 1
    if (businessUnitId === 1) {
      const totalItem = inventoryData.find(item =>
        item.unit && (item.unit.toLowerCase().includes('total') || item.unit.toLowerCase().includes('all'))
      );
      if (totalItem) {
        console.log(`📊 Using total inventory for Unit ${businessUnitId}:`, totalItem.quantity, 'grams');
        return totalItem.quantity;
      }
    }

    // ✅ Final fallback: no data found
    console.log(`📊 No inventory data found for Unit ${businessUnitId}, returning 0`);
    return 0;
  };

  useEffect(() => {
    fetchBusinessUnits();
    fetchProfitSummary();
    fetchTotalInvestment();
    fetchInventoryData();
    
    const initializeGoldRate = async () => {
      const rate = await fetchLiveGoldRate();
      setLiveGoldRate(rate);
      
      setTimeout(() => {
        fetchProfitSummary();
      }, 1000);
    };
    
    initializeGoldRate();
  }, []);

  useEffect(() => {
    if (liveGoldRate && businessUnits.length > 0 && inventoryData.length > 0) {
      fetchProfitSummary();
    }
  }, [liveGoldRate, businessUnits, inventoryData]);

  useEffect(() => {
    if (selectedUnit && selectedUnit !== '' && inventoryData.length > 0) {
      calculateProvisionalProfit();
      fetchBookings();
    } else {
      setProvisionalData(null);
      setCalculationBreakdown(null);
      setBookings([]);
    }
  }, [selectedUnit, liveGoldRate, inventoryData]);

  const fetchBusinessUnits = async () => {
    try {
      const data = await apiGetBusinessUnits();
      setBusinessUnits(data);
    } catch (err) {
      console.error('Error fetching business units:', err);
    }
  };

  const fetchTotalInvestment = async () => {
    try {
      const data = await apiGetTotalInvestmentAll();
      console.log('📊 Profit Management Investment API response:', data);
      setTotalInvestment(data.total_investment || data.total_invested || 0);
    } catch (err) {
      console.error('Error fetching total investment:', err);
      if (summary.length > 0) {
        const totalInvested = summary.reduce((sum, unit) => sum + (unit.provisional_profit || 0), 0);
        setTotalInvestment(totalInvested);
      }
    }
  };

// ✅ CORRECTED: Profit summary with consistent investment data
const fetchProfitSummary = async () => {
  try {
    const data = await apiGetProfitSummary();
    console.log('✅ Backend profit summary:', data);
    
    if (!liveGoldRate?.goldPerGram) {
      console.warn('⚠️ No live gold rate available');
      setSummary(data);
      return;
    }

    // Recalculate with PROPER unit-specific data
    const recalculatedData = await Promise.all(data.map(async (unit: ProfitSummary) => {
      const unitWeight = getInventoryWeightForUnit(unit.business_unit_id);
      console.log(`📊 Unit ${unit.business_unit_id} inventory weight:`, unitWeight, 'grams');
      
      // ✅ CONSISTENT: Get REAL investment data for THIS specific unit
      let unitInvestment = 0;
      let unitExpenses = 0;
      
      try {
        // Get investments specifically for this business unit
        const unitInvestments = await apiGetInvestments(unit.business_unit_id);
        unitInvestment = unitInvestments.reduce((sum: number, investment: any) => {
          return sum + (investment.amount_transferred || investment.amount || 0);
        }, 0);
        
        // Get expenses for this unit
        const unitExpensesData = await apiGetExpenses(unit.business_unit_id);
        unitExpenses = unitExpensesData.reduce((sum: number, expense: any) => {
          return sum + (expense.amount || 0);
        }, 0);
        
        console.log(`📊 Unit ${unit.business_unit_id} REAL data - Investment: ${unitInvestment}, Expenses: ${unitExpenses}`);
        
      } catch (error) {
        console.error(`❌ Error fetching real data for Unit ${unit.business_unit_id}:`, error);
        // Consistent fallback: use 0 for failed fetches
        unitInvestment = 0;
        unitExpenses = 0;
      }
          
      // Handle zero inventory properly
      if (unitWeight === 0) {
        console.log(`📊 Unit ${unit.business_unit_id} has ZERO inventory - all profit values set to 0`);
        return {
          ...unit,
          provisional_profit: 0,
          partner_provisional_share: 0,
          balance_share_amount: 0,
          available_for_withdrawal: unit.available_for_withdrawal || 0,
          total_investment: unitInvestment, // Still show actual investment
          total_expenses: unitExpenses      // Still show actual expenses
        };
      }
      
      // Recalculate with live data
      const liveInventoryValue = unitWeight * liveGoldRate.goldPerGram;
      const recalculatedProvisionalProfit = Math.max(0, liveInventoryValue - unitInvestment - unitExpenses);
      
      // Use correct share percentages
      let correctedShare = unit.share_percentage;
      if (correctedShare === 0) {
        correctedShare = unit.business_unit_id === 1 ? 33 : 16;
      }
      
      const partnerProvisionalShare = recalculatedProvisionalProfit * (correctedShare / 100);
      const balanceShareAmount = Math.max(0, partnerProvisionalShare - (unit.booked_profit || 0));
      const availableForWithdrawal = Math.max(0, (unit.booked_profit || 0) - (unit.withdrawn_profit || 0));

      console.log(`📊 ✅ Unit ${unit.business_unit_id} FINAL CORRECT calculation:`, {
        weight: unitWeight,
        inventoryValue: liveInventoryValue,
        investment: unitInvestment,
        expenses: unitExpenses,
        sharePercentage: correctedShare,
        provisionalProfit: recalculatedProvisionalProfit,
        partnerShare: partnerProvisionalShare
      });

      return {
        ...unit,
        provisional_profit: recalculatedProvisionalProfit,
        share_percentage: correctedShare,
        partner_provisional_share: partnerProvisionalShare,
        balance_share_amount: balanceShareAmount,
        available_for_withdrawal: availableForWithdrawal,
        total_investment: unitInvestment,
        total_expenses: unitExpenses
      };
    }));
    
    setSummary(recalculatedData);
    
    // Calculate totals
    const totals = recalculatedData.reduce((acc: any, unit: ProfitSummary) => {
      return {
        total_provisional_profit: acc.total_provisional_profit + (unit.provisional_profit || 0),
        total_partner_share: acc.total_partner_share + (unit.partner_provisional_share || 0),
        total_booked_profit: acc.total_booked_profit + (unit.booked_profit || 0),
        total_withdrawn_profit: acc.total_withdrawn_profit + (unit.withdrawn_profit || 0),
        total_available_for_withdrawal: acc.total_available_for_withdrawal + (unit.available_for_withdrawal || 0),
        total_balance_share_amount: acc.total_balance_share_amount + (unit.balance_share_amount || 0)
      };
    }, {
      total_provisional_profit: 0,
      total_partner_share: 0,
      total_booked_profit: 0,
      total_withdrawn_profit: 0,
      total_available_for_withdrawal: 0,
      total_balance_share_amount: 0
    });
    
    setSummaryTotals(totals);
    
    console.log('✅ FINAL CORRECTED summary totals:', totals);
    
  } catch (err: any) {
    console.error('Error fetching profit summary:', err);
    setError(err.message || 'Failed to fetch profit summary');
  }
};

  const fetchBookings = async () => {
    try {
      const data = await apiGetProfitBookings(selectedUnit ? parseInt(selectedUnit) : undefined);
      setBookings(data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch profit bookings');
    }
  };

  const fetchWithdrawals = async () => {
    try {
      const data = await apiGetProfitWithdrawals();
      setWithdrawals(data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch withdrawals');
    }
  };

  // ✅ CRITICAL FIX: Modified booking function to use FRONTEND validation
  const handleBookProfit = async () => {
  try {
    setLoading(true);
    setError('');
    
    const bookingAmount = parseFloat(bookingForm.amount);
    const availableAmount = provisionalData?.available_for_booking || 0;
    
    // ✅ FRONTEND VALIDATION ONLY
    if (bookingAmount > availableAmount) {
      throw new Error(`Booking amount exceeds your available profit share. Maximum: ${formatCurrency(availableAmount)}`);
    }
    
    if (bookingAmount <= 0) {
      throw new Error('Booking amount must be greater than 0');
    }

    console.log('🔍 ✅ FRONTEND VALIDATION PASSED:', {
      bookingAmount,
      availableAmount,
      frontendCalculation: provisionalData?.available_for_booking
    });

    // ✅ CRITICAL: Send ALL frontend calculation data to backend
    const bookingData = {
      business_unit_id: bookingForm.business_unit_id,
      booking_date: bookingForm.booking_date,
      amount: bookingAmount.toString(),
      description: bookingForm.description,
      // ✅ SEND FRONTEND CALCULATION DATA
      gold_rate: liveGoldRate?.goldPerGram || 0,
      inventory_value: provisionalData?.inventory_value || 0,
      provisional_profit: provisionalData?.provisional_profit || 0,
      partner_share: provisionalData?.partner_share || 0,
      share_percentage: provisionalData?.share_percentage || 0,
      total_investment: provisionalData?.total_investment || 0,
      total_expenses: provisionalData?.total_expenses || 0,
      // ✅ ADD THESE FOR BACKEND VALIDATION
      frontend_calculation: true, // Flag to indicate this uses frontend data
      live_gold_rate: liveGoldRate?.goldPerGram || 0
    };
    
    console.log('🔍 ✅ SENDING COMPLETE FRONTEND DATA TO BACKEND:', bookingData);
    
    await apiBookProfit(bookingData);

    setSuccess(`Profit of د.${formatCurrency(bookingAmount)} booked successfully using live gold rate`);
    setBookingOpen(false);
    setBookingForm({
      business_unit_id: '',
      booking_date: new Date().toISOString().split('T')[0],
      amount: '',
      description: ''
    });
    
    await calculateProvisionalProfit();
    fetchBookings();
    fetchProfitSummary();
    
  } catch (err: any) {
    console.error('❌ BOOKING ERROR:', err);
    setError(err.message || 'Failed to book profit');
  } finally {
    setLoading(false);
  }
};

  const handleWithdrawProfit = async () => {
    try {
      setLoading(true);
      await apiWithdrawProfit(withdrawalForm);

      setSuccess('Withdrawal request created successfully');
      setWithdrawalOpen(false);
      setWithdrawalForm({
        profit_booking_id: '',
        withdrawal_date: new Date().toISOString().split('T')[0],
        amount: '',
        description: ''
      });
      fetchWithdrawals();
      fetchProfitSummary();
    } catch (err: any) {
      setError(err.message || 'Failed to withdraw profit');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenBooking = () => {
    if (!selectedUnit) {
      setError('Please select a business unit first');
      return;
    }
    if (!liveGoldRate) {
      setError('Please refresh the gold rate first');
      return;
    }
    
    // ✅ SET FRONTEND CALCULATED AMOUNT
    const availableAmount = provisionalData?.available_for_booking || 0;
    setBookingForm({
      ...bookingForm,
      business_unit_id: selectedUnit,
      amount: availableAmount.toString()
    });
    setBookingOpen(true);
  };

  const handleOpenWithdrawal = (booking: ProfitBooking) => {
    setSelectedBooking(booking);
    setWithdrawalForm({
      ...withdrawalForm,
      profit_booking_id: booking.id.toString(),
      amount: ''
    });
    setWithdrawalOpen(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'success';
      case 'completed': return 'success';
      case 'withdrawn': return 'info';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  // Refresh inventory data button
  const refreshInventoryData = async () => {
    try {
      setLoading(true);
      await fetchInventoryData();
      setSuccess('Inventory data refreshed successfully');
      
      if (selectedUnit) {
        await calculateProvisionalProfit();
      }
      fetchProfitSummary();
    } catch (err: any) {
      setError('Failed to refresh inventory data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const CalculationBreakdownCard = ({ data, actualInvestment }: { data: CalculationBreakdown; actualInvestment: number }) => {
  // 🚨 CRITICAL FIX: Use ONLY the unit-specific data, NOT the global actualInvestment
  const effectiveInvestment = data.total_investment; // Use ONLY the unit-specific investment
  const effectiveProfit = data.inventory_value - effectiveInvestment - data.total_expenses;
  
  console.log('🔍 🔴 CALCULATION BREAKDOWN CARD DEBUG:', {
    unitSpecificInvestment: data.total_investment, // This should be 0 for Unit B
    globalInvestment: actualInvestment, // This is 41,800,000 (WRONG for Unit B)
    effectiveInvestment: effectiveInvestment,
    effectiveProfit: effectiveProfit
  });

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 0, mr: 1 }}>
            Provisional Profit Calculation Breakdown (Live Gold Rate & Dynamic Inventory)
          </Typography>
          <Tooltip title="Formula: Inventory Value - Total Investment - Total Expenses = Provisional Profit. Using live gold rate from Binance API and dynamic inventory data." arrow>
            <IconButton size="small">
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="textSecondary">
              Total Inventory Value:
            </Typography>
            <Typography variant="h6">
              د.{formatCurrency(data.inventory_value)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Using live gold rate & dynamic inventory
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="textSecondary">
              Total Investment:
            </Typography>
            <Typography variant="h6">
              د.{formatCurrency(data.total_investment)} {/* ✅ NOW USING UNIT-SPECIFIC DATA */}
            </Typography>
            <Typography variant="caption" color={data.total_investment === 0 ? "success.main" : "text.secondary"}>
              Unit-specific investment data
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="textSecondary">
              Total Expenses:
            </Typography>
            <Typography variant="h6">
              د.{formatCurrency(data.total_expenses)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="textSecondary">
              Provisional Profit:
            </Typography>
            <Typography variant="h6" color={effectiveProfit > 0 ? "success.main" : "text.secondary"} sx={{ fontWeight: 'bold' }}>
              د.{formatCurrency(effectiveProfit)}
            </Typography>
          </Grid>
        </Grid>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Formula: Inventory Value ({formatCurrency(data.inventory_value)}) - Total Investment ({formatCurrency(data.total_investment)}) - Total Expenses ({formatCurrency(data.total_expenses)}) = {formatCurrency(effectiveProfit)}
        </Typography>
        {liveGoldRate && (
          <Typography variant="caption" color="primary" sx={{ mt: 1, display: 'block', fontWeight: 'bold' }}>
            Current Gold Rate: د.{formatCurrency(liveGoldRate.goldPerGram)}/gram (Source: {liveGoldRate.source})
          </Typography>
        )}
        
        
      </CardContent>
    </Card>
  );
};

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Profit Management</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {/* Add Inventory Refresh Button */}
          <Button
            variant="outlined"
            onClick={refreshInventoryData}
            startIcon={<RefreshIcon />}
            disabled={loading}
          >
            Refresh Inventory Data
          </Button>
          <Button
            variant="outlined"
            onClick={refreshGoldRate}
            startIcon={refreshingGoldRate ? <CircularProgress size={16} /> : <RefreshIcon />}
            disabled={refreshingGoldRate}
          >
            Refresh Gold Rate
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              fetchProfitSummary();
              fetchTotalInvestment();
            }}
            startIcon={<RefreshIcon />}
          >
            Refresh Data
          </Button>
        </Box>
      </Box>

      
      {/* Live Gold Rate Display */}
      {liveGoldRate && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h6">Live Gold Rate</Typography>
                <Typography variant="h4">
                  د.{formatCurrency(liveGoldRate.goldPerGram)}
                  <Typography variant="body2" component="span" sx={{ ml: 1 }}>
                    per gram
                  </Typography>
                </Typography>
                <Typography variant="body2">
                  د.{formatCurrency(liveGoldRate.goldPerOz)} per oz • {liveGoldRate.source}
                </Typography>
                <Typography variant="caption">
                  Last updated: {new Date(liveGoldRate.lastUpdated).toLocaleTimeString()}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  All profit calculations use this live rate
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Profit Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {summary.map((unit) => (
          <Grid item xs={12} md={6} lg={4} key={unit.business_unit_id}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {unit.business_unit_name}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Share: {unit.share_percentage}%
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    Unit Provisional Profit: د.{formatCurrency(unit.provisional_profit)}
                  </Typography>
                  <Typography variant="body2">
                    Your Provisional Share: د.{formatCurrency(unit.partner_provisional_share)}
                  </Typography>
                  <Typography variant="body2">
                    Your Booked Profit: د{formatCurrency(unit.booked_profit)}
                  </Typography>
                  <Typography variant="body2">
                    Your Withdrawn: د{formatCurrency(unit.withdrawn_profit)}
                  </Typography>
                  <Typography variant="body2" color="info.main" sx={{ fontWeight: 'bold' }}>
                    Balance Share Amount: د{formatCurrency(unit.balance_share_amount)}
                  </Typography>
                  <Typography variant="body2" color="primary" sx={{ fontWeight: 'bold', mt: 1 }}>
                    Available for Withdrawal: د{formatCurrency(unit.available_for_withdrawal)}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Totals Card */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12}>
          <Card sx={{ 
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: 2
          }}>
            <CardContent>
              <Typography variant="h5" gutterBottom sx={{ 
                fontWeight: 'bold',
                color: 'primary.main',
                mb: 3
              }}>
                Totals Across All Business Units (Dynamic Calculation)
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="textSecondary">
                    Total Provisional Profit:
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    د.{formatCurrency(summaryTotals.total_provisional_profit)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Using live gold rate & dynamic inventory
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="textSecondary">
                    Your Total Share:
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    د.{formatCurrency(summaryTotals.total_partner_share)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="textSecondary">
                    Total Booked Profit:
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    د.{formatCurrency(summaryTotals.total_booked_profit)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="textSecondary">
                    Total Available:
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                    د.{formatCurrency(summaryTotals.total_available_for_withdrawal)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="textSecondary">
                    Total Balance Share:
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'info.main' }}>
                    د.{formatCurrency(summaryTotals.total_balance_share_amount)}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 3 }}>
        <Tab label="Profit Booking" />
        <Tab label="Withdrawals" />
      </Tabs>

      {tabValue === 0 && (
        <>
          <Box sx={{ mb: 3 }}>
            <TextField
              select
              label="Select Business Unit"
              value={selectedUnit}
              onChange={(e) => setSelectedUnit(e.target.value)}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">Select Business Unit</MenuItem>
              {businessUnits.map((unit) => (
                <MenuItem key={unit.id} value={unit.id}>
                  {unit.name}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          {selectedUnit && provisionalData && calculationBreakdown && (
  <>
    {/* 🚨 FIX: Remove the actualInvestment prop or pass the correct unit-specific value */}
    <CalculationBreakdownCard 
      data={calculationBreakdown} 
      actualInvestment={calculationBreakdown.total_investment} // ✅ Use unit-specific data
    />

              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">Your Share Details</Typography>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={handleOpenBooking}
                      disabled={provisionalData.available_for_booking <= 0}
                    >
                      Book Available Profit
                    </Button>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2" color="textSecondary">
                        Your Share Percentage:
                      </Typography>
                      <Typography variant="h6">
                        {formatNumber(provisionalData.share_percentage)}%
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2" color="textSecondary">
                        Your Share Amount:
                      </Typography>
                      <Typography variant="h6">
                        د.{formatCurrency(provisionalData.partner_share)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2" color="textSecondary">
                        Already Booked:
                      </Typography>
                      <Typography variant="h6">
                        د.{formatCurrency(provisionalData.already_booked)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2" color="textSecondary">
                        Available for Booking:
                      </Typography>
                      <Typography variant="h6" color={provisionalData.available_for_booking > 0 ? "success.main" : "text.secondary"}>
                        د.{formatCurrency(provisionalData.available_for_booking)}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </>
          )}

         <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Profit Booking History
              </Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Business Unit</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Gold Rate</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {bookings.map((booking) => (
                      <TableRow key={booking.id}>
                        <TableCell>{new Date(booking.booking_date).toLocaleDateString()}</TableCell>
                        <TableCell>{booking.business_unit_name}</TableCell>
                        <TableCell>د.{formatCurrency(booking.amount)}</TableCell>
                        <TableCell>د.{formatCurrency(booking.gold_rate)}/g</TableCell>
                        <TableCell>
                          <Chip
                            label={booking.status}
                            size="small"
                            color={getStatusColor(booking.status) as any}
                          />
                        </TableCell>
                        <TableCell>
                          {booking.status === 'approved' && (
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<WithdrawIcon />}
                              onClick={() => handleOpenWithdrawal(booking)}
                            >
                              Withdraw
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </>
      )}

      {tabValue === 1 && (
  <Card>
    <CardContent>
      <Typography variant="h6" gutterBottom>
        Withdrawal History
      </Typography>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {withdrawals.map((withdrawal) => (
              <TableRow key={withdrawal.id}>
                <TableCell>{new Date(withdrawal.withdrawal_date).toLocaleDateString()}</TableCell>
                <TableCell>د.{formatCurrency(withdrawal.amount)}</TableCell>
                <TableCell>{withdrawal.description}</TableCell>
                <TableCell>
                  <Chip
                    label={withdrawal.status}
                    size="small"
                    color={getStatusColor(withdrawal.status) as any}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </CardContent>
  </Card>
)}

      {/* Booking Dialog */}
      <Dialog open={bookingOpen} onClose={() => setBookingOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Book Available Profit</DialogTitle>
        <DialogContent>
          <TextField
            label="Booking Date"
            type="date"
            fullWidth
            margin="normal"
            value={bookingForm.booking_date}
            onChange={(e) => setBookingForm({ ...bookingForm, booking_date: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Amount"
            type="number"
            fullWidth
            margin="normal"
            value={bookingForm.amount}
            onChange={(e) => setBookingForm({ ...bookingForm, amount: e.target.value })}
            helperText={`Available: د.${formatCurrency(provisionalData?.available_for_booking || 0)}`}
          />
          <TextField
            label="Description"
            fullWidth
            margin="normal"
            multiline
            rows={3}
            value={bookingForm.description}
            onChange={(e) => setBookingForm({ ...bookingForm, description: e.target.value })}
          />
          {liveGoldRate && (
            <Typography variant="caption" color="text.secondary">
              Current Gold Rate: د.{formatCurrency(liveGoldRate.goldPerGram)}/gram
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBookingOpen(false)}>Cancel</Button>
          <Button onClick={handleBookProfit} variant="contained" disabled={loading}>
            {loading ? 'Booking...' : 'Book Profit'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Withdrawal Dialog */}
      <Dialog open={withdrawalOpen} onClose={() => setWithdrawalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Withdraw Booked Profit</DialogTitle>
        <DialogContent>
          {selectedBooking && (
            <Typography variant="body2" sx={{ mb: 2 }}>
              Available from booking: د.{formatCurrency(selectedBooking.amount)}
            </Typography>
          )}
          <TextField
            label="Withdrawal Date"
            type="date"
            fullWidth
            margin="normal"
            value={withdrawalForm.withdrawal_date}
            onChange={(e) => setWithdrawalForm({ ...withdrawalForm, withdrawal_date: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Amount"
            type="number"
            fullWidth
            margin="normal"
            value={withdrawalForm.amount}
            onChange={(e) => setWithdrawalForm({ ...withdrawalForm, amount: e.target.value })}
          />
          <TextField
            label="Description"
            fullWidth
            margin="normal"
            multiline
            rows={3}
            value={withdrawalForm.description}
            onChange={(e) => setWithdrawalForm({ ...withdrawalForm, description: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWithdrawalOpen(false)}>Cancel</Button>
          <Button onClick={handleWithdrawProfit} variant="contained" disabled={loading}>
            {loading ? 'Processing...' : 'Withdraw'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default ProfitManagement;